import React from "react";
import { connect } from "react-redux";
import { sendLog } from "@learningblocks/shared-components/lib/EventLogger/utils";
import { LogContext } from "@learningblocks/shared-components/lib/EventLogger/Log";
import { HotspotCommonSection as Component } from "./HotspotCommonSection";

const findNextValidMenu = (menuList, currentIndex) => {
  const nextValidMenu = menuList.slice(currentIndex + 1).find(
    // Keep only if NOT (locked OR invisible)
    (m) => !(m.locked === true || m.visible === false)
  );
  return nextValidMenu ? nextValidMenu.value : undefined;
};

const findPreviousValidMenu = (menuList, currentIndex) => {
  const previousValidMenu = menuList
    .slice(0, currentIndex)
    .reverse()
    .find((m) => !(m.locked === true || m.visible === false));
  return previousValidMenu ? previousValidMenu.value : undefined;
};

const calculateNextMenuValue = (menuList) => {
  for (let i = 0; i < menuList.length; i++) {
    if (menuList[i].current) {
      return findNextValidMenu(menuList, i);
    }
  }
  return undefined;
};

const calculatePreviousMenuValue = (menuList) => {
  for (let i = 0; i < menuList.length; i++) {
    if (menuList[i].current) {
      return findPreviousValidMenu(menuList, i);
    }
  }
  return undefined;
};

// --- Dependency helpers ---
const isDependencySatisfied = (
  dep,
  { cscq, mscq, oeq, generatedVariables },
  menuList
) => {
  // Notebook random variable dependency: { randVarName, randVarValue }
  if (typeof dep?.randVarName === "string") {
    const gv = generatedVariables?.[dep.randVarName];
    if (Object.prototype.hasOwnProperty.call(dep, "randVarValue")) {
      const want = dep.randVarValue;
      const got = gv;
      return got == want;
    }
    // If only randVarName is provided, require the variable to exist
    return gv !== undefined;
  }

  // Section completion dependency:
  // Supports any of: { sectionIndex }, { sectionNumber }, { sectionValue } (matches title or value)
  if (
    typeof dep?.sectionIndex === "number" ||
    typeof dep?.sectionNumber === "number" ||
    typeof dep?.sectionValue === "string"
  ) {
    let section = null;
    if (typeof dep.sectionIndex === "number") {
      section = menuList?.[dep.sectionIndex] ?? null;
    }
    if (!section && typeof dep.sectionNumber === "number") {
      section = menuList?.find((s) => s?.number === dep.sectionNumber) ?? null;
    }
    if (!section && typeof dep.sectionValue === "string") {
      const want =
        typeof dep.sectionValue === "string"
          ? dep.sectionValue.trim()
          : dep.sectionValue;
      section =
        menuList?.find((s) => {
          const t = typeof s?.title === "string" ? s.title.trim() : s?.title;
          const v = typeof s?.value === "string" ? s.value.trim() : s?.value;
          return t === want || v === want;
        }) ?? null;
    }
    if (!section) return false;

    const curr = Number(section.currentProgress ?? 0);
    // Prefer explicit totalOverride; then maxProgress; then activitiesCount; else 0
    const max = Number(
      section.totalOverride ??
        section.maxProgress ??
        section.activitiesCount ??
        0
    );

    // Require a meaningful target (>0)
    return max > 0 && curr >= max;
  }

  // Component/answer dependency: { componentId, answerId, any }
  const { componentId, answerId, any } = dep ?? {};
  if (componentId == null) return false;

  if (any) {
    // If "any" is true, check if any answerId is present (not null/undefined/empty)
    const hasCSCQ =
      !!cscq?.[componentId]?.disabled &&
      Array.isArray(cscq[componentId].disabled) &&
      cscq[componentId].disabled.length > 0;
    const hasMSCQ =
      !!mscq?.[componentId]?.disabled &&
      Array.isArray(mscq[componentId].disabled) &&
      mscq[componentId].disabled.length > 0;
    const hasOEQ = !!oeq?.[componentId]?.complete;

    return hasCSCQ || hasMSCQ || hasOEQ;
  } else {
    // Support answerId as a single value OR an array of values.
    if (answerId == null) return false;
    const answerIds = Array.isArray(answerId) ? answerId : [answerId];

    const inCSCQ =
      !!cscq?.[componentId]?.disabled &&
      Array.isArray(cscq[componentId].disabled) &&
      answerIds.some((id) => cscq[componentId].disabled.includes(id));

    const inMSCQ =
      !!mscq?.[componentId]?.disabled &&
      Array.isArray(mscq[componentId].disabled) &&
      answerIds.some((id) => mscq[componentId].disabled.includes(id));
    const inOEQ = !!oeq?.[componentId]?.complete;

    return inCSCQ || inMSCQ || inOEQ;
  }
};

const areDependenciesSatisfied = (
  deps,
  stores,
  menuList,
  negativeAlignment,
  dependencyLogic = "AND"
) => {
  if (!Array.isArray(deps) || deps.length === 0) return false;

  let depsPass;
  if (dependencyLogic === "OR") {
    // OR logic: at least one dependency must be satisfied
    depsPass = deps.some((d) => isDependencySatisfied(d, stores, menuList));
  } else {
    // AND logic (default): all dependencies must be satisfied
    depsPass = deps.every((d) => isDependencySatisfied(d, stores, menuList));
  }

  if (negativeAlignment) {
    return !depsPass;
  }
  return depsPass;
};

const hasDeps = (obj) =>
  Array.isArray(obj?.dependencies) && obj.dependencies.length > 0;

// Environment check: only allow bypass on non-production environments
const isNonProduction = () => {
  if (typeof window === "undefined") return false;
  const hostname = window.location.hostname;
  return (
    hostname === "localhost" ||
    hostname.includes("labxchange-stage.") ||
    hostname.includes("labxchange-dev.") ||
    hostname.includes("review-app")
  );
};

// Track which sections have been logged to ensure we only log once per section
const loggedSections = new Set();

// Helper function to clear logged sections (for testing purposes)
export function clearLoggedSections() {
  loggedSections.clear();
}

export function mapStateToProps(state, ownProps) {
  const { cscq } = state;
  const { mscq } = state;
  const { oeq } = state;
  const { questionTable } = state;
  const { generatedVariables } = state.notebook || {};

  // Clone up front so we can freely mutate and guarantee a new reference
  let updatedMenuList = Array.isArray(ownProps.menuList)
    ? ownProps.menuList.map((i) => ({ ...i }))
    : [];

  // Check for bypass query parameter (only works in non-production)
  if (typeof window !== "undefined" && isNonProduction()) {
    const urlParams = new URLSearchParams(window.location.search);
    const unlockAllSections = urlParams.get("uls") === "true";

    if (unlockAllSections) {
      // Unlock all sections by removing BOTH 'locked' property AND 'dependencies'
      // This prevents the dependency checking logic from re-locking them later
      updatedMenuList = updatedMenuList.map((item) => {
        const unlocked = { ...item, locked: false };
        delete unlocked.dependencies;
        return unlocked;
      });

      // Log for debugging (only visible in non-prod environments)
      console.log(
        "[HotspotCommonSectionContent] Section bypass active: all sections unlocked"
      );
    }
  }

  let updatedComponentList = [];

  if (ownProps.components) {
    updatedComponentList = [];
    ownProps.components.forEach((component) => {
      // Components without dependencies are always included
      if (!hasDeps(component)) {
        updatedComponentList.push(component);
        return;
      }

      // Check if ALL dependency rules are satisfied
      let allRulesSatisfied = true;
      component.dependencies.forEach((depRule) => {
        const satisfied = areDependenciesSatisfied(
          depRule.dependsOn,
          { cscq, mscq, oeq, generatedVariables },
          updatedMenuList,
          depRule.negativeAlignment,
          depRule.dependencyLogic
        );

        if (!satisfied) {
          allRulesSatisfied = false;
        }
      });

      // Only add component if ALL rules are satisfied
      if (allRulesSatisfied) {
        const modifiedComponent = { ...component };

        // Apply all toggle props from satisfied rules
        component.dependencies.forEach((depRule) => {
          if (Array.isArray(depRule.toggleProps)) {
            depRule.toggleProps.forEach((prop) => {
              modifiedComponent[prop] = !component[prop];
            });
          }
        });

        // Remove dependencies to prevent re-processing
        delete modifiedComponent.dependencies;
        updatedComponentList.push(modifiedComponent);
      }
      // If not all satisfied, component is filtered out (not added to list)
    });

    // Decrement current section progress if a previously complete prediction is no longer satisfied
    ownProps.components
      .filter(
        (component) =>
          component.isComplete &&
          component.isActivity &&
          component.isPredictionQuestion
      )
      .forEach((component) => {
        const componentId = component.id;
        let isActivityCompleted = false;

        if (cscq[componentId] && component.answerId) {
          isActivityCompleted = component.answerId.some((answerId) =>
            cscq[componentId].disabled
              ? cscq[componentId].disabled.includes(answerId)
              : false
          );
        }

        if (!isActivityCompleted) {
          updatedMenuList = updatedMenuList.map((item) => {
            if (
              item.value === ownProps.currentMenu.value &&
              item.currentProgress > 0
            ) {
              if (typeof ownProps.updateProgress === "function") {
                ownProps.updateProgress({
                  menu: ownProps.currentMenu.value,
                  menuProgress: item.currentProgress - 1,
                  overallProgress: ownProps.currentProgress - 1,
                });
              }
              ownProps.currentProgress -= 1;
              component.isComplete = false;
              return { ...item, currentProgress: item.currentProgress - 1 };
            }
            return item;
          });
        }
      });

    // Increment current section progress when an activity becomes complete
    ownProps.components
      .filter((component) => !component.isComplete && component.isActivity)
      .forEach((component) => {
        const componentId = component.id;

        let isActivityCompleted = false;
        if (cscq[componentId] && component.answerId) {
          isActivityCompleted = component.answerId.some((answerId) =>
            cscq[componentId].disabled
              ? cscq[componentId].disabled.includes(answerId)
              : false
          );
        }
        if (mscq[componentId] && component.answerId) {
          const disabledList = mscq[componentId].disabled || [];

          // Check if all correct answers are disabled
          const allCorrectDisabled = component.answerId.every((answerId) =>
            disabledList.includes(answerId)
          );

          // Check if ONLY correct answers are disabled (no incorrect answers)
          const onlyCorrectDisabled = disabledList.every((disabledId) =>
            component.answerId.includes(disabledId)
          );

          isActivityCompleted = allCorrectDisabled && onlyCorrectDisabled;
        }
        if (oeq && oeq[componentId] && oeq[componentId].complete) {
          isActivityCompleted = true;
        }

        if (
          questionTable &&
          questionTable[componentId] &&
          questionTable[componentId].correct
        ) {
          isActivityCompleted = true;
        }

        if (isActivityCompleted) {
          updatedMenuList = updatedMenuList.map((item) => {
            if (
              item.value === ownProps.currentMenu.value &&
              item.maxProgress > item.currentProgress
            ) {
              const newCurrentProgress = item.currentProgress + 1;
              const isSectionComplete = item.maxProgress === newCurrentProgress;

              if (typeof ownProps.updateProgress === "function") {
                ownProps.updateProgress({
                  menu: ownProps.currentMenu.value,
                  menuProgress: newCurrentProgress,
                  overallProgress: ownProps.currentProgress + 1,
                });
              }
              ownProps.currentProgress += 1;
              component.isComplete = true;

              if (isSectionComplete && !loggedSections.has(item.value)) {
                loggedSections.add(item.value);
                const logData = {
                  event_type: `sim_${item.value || ""}_section_completed`,
                  event_schema: "track",
                  sim_id: ownProps.sim_id,
                  sim_name: ownProps.sim_name,
                  sim_session_id: ownProps.sim_session_id,
                  level: ownProps.level,
                  section: (item.value || "").toLowerCase(),
                };
                sendLog(logData);
              }

              return { ...item, currentProgress: newCurrentProgress };
            }
            return item;
          });
        }
      });
  }

  // ── IMPORTANT: Run gating on EVERY render, not only when `ownProps.components` exists ──
  if (updatedMenuList && updatedMenuList.length) {
    // Filter sections with dependency rules
    const dependantMenuItems = updatedMenuList.filter(
      (i) => Array.isArray(i.dependencies) && i.dependencies.length > 0
    );

    // Build a new array so changes create a new reference
    let nextMenu = updatedMenuList.map((i) => ({ ...i }));
    const eq = (a, b) =>
      typeof a === "string" && typeof b === "string"
        ? a.trim() === b.trim()
        : a === b;

    dependantMenuItems.forEach((depItem) => {
      // Check each dependency rule
      depItem.dependencies.forEach((depRule) => {
        const satisfied = areDependenciesSatisfied(
          depRule.dependsOn,
          { cscq, mscq, oeq, generatedVariables },
          updatedMenuList,
          depRule.negativeAlignment,
          depRule.dependencyLogic
        );

        if (satisfied) {
          // Toggle ONLY the listed dependent item properties
          const idx = nextMenu.findIndex((m) => eq(m.value, depItem.value));
          if (idx !== -1) {
            const before = nextMenu[idx];
            const after = { ...before };
            if (Array.isArray(depRule.toggleProps)) {
              depRule.toggleProps.forEach((prop) => {
                after[prop] = !before[prop]; // Toggle (invert) the value
              });
            }
            // Check if any toggled property actually changed
            const changed = depRule.toggleProps?.some(
              (prop) => before[prop] !== after[prop]
            );
            if (changed) {
              nextMenu[idx] = after;
            }
          }
        }
      });
    });

    updatedMenuList = nextMenu;
  }

  const nextMenuValue = calculateNextMenuValue(updatedMenuList);
  const prevMenuValue = calculatePreviousMenuValue(updatedMenuList);

  return {
    ...ownProps,
    menuList: updatedMenuList,
    components: updatedComponentList,
    displayProgress: true,
    cscq: cscq,
    generatedVariables: generatedVariables,
    nextMenuValue,
    prevMenuValue,
  };
}

const ConnectedComponent = connect(mapStateToProps)(Component);

export const HotspotCommonSectionContent = (props) => (
  <LogContext.Consumer>
    {(logContext) => (
      <ConnectedComponent
        {...props}
        sim_id={logContext?.sim_id}
        sim_name={logContext?.sim_name}
        sim_session_id={logContext?.sim_session_id}
        level={logContext?.level}
        section={logContext?.section}
      />
    )}
  </LogContext.Consumer>
);
